import React, { JSX } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { ExpandLess, ExpandMore, KeyboardArrowDown } from '@mui/icons-material';
import { NavigationItem } from 'types/navigation';
import { DropdownButton } from './HeaderStyles';

interface HeaderNavigationProps {
  navigationItems: NavigationItem[];
  dropdownAnchors: { [key: string]: HTMLElement | null };
  mobileExpanded: { [key: string]: boolean };
  userExists: boolean;
  isCurrentPath: (item: NavigationItem) => boolean;
  hasActiveChild: (item: NavigationItem) => boolean;
  onDropdownOpen: (event: React.MouseEvent<HTMLElement>, itemId: string) => void;
  onDropdownClose: (itemId: string) => void;
  onMobileToggle: (itemId: string) => void;
  onNavigate: (path: string) => void;
}

export function DesktopNavigation(props: HeaderNavigationProps): JSX.Element {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}
    >
      {props.navigationItems.map((item, index) => (
        <DesktopNavigationItem key={item.id} item={item} index={index} {...props} />
      ))}
    </Box>
  );
}

function DesktopNavigationItem(
  props: HeaderNavigationProps & { item: NavigationItem; index: number }
): JSX.Element {
  const { item, index } = props;
  const hasChildren = item.children && item.children.length > 0;
  const isActive = props.isCurrentPath(item) || props.hasActiveChild(item);
  const marginRight = index === 0 || index === props.navigationItems.length - 1 ? 'auto' : 'unset';

  if (!hasChildren) {
    return (
      <DropdownButton
        onClick={() => item.path && props.onNavigate(item.path)}
        sx={{
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          color: isActive ? 'secondary.main' : 'primary.contrastText',
          mr: marginRight,
        }}
      >
        {item.icon && <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>}
        {item.label}
      </DropdownButton>
    );
  }

  return (
    <Box sx={{ mr: marginRight }}>
      <DropdownButton
        onClick={e => props.onDropdownOpen(e, item.id)}
        endIcon={<KeyboardArrowDown />}
        sx={{
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          color: isActive ? 'secondary.main' : 'primary.contrastText',
        }}
      >
        {item.icon && <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>}
        {item.label}
      </DropdownButton>
      <Menu
        anchorEl={props.dropdownAnchors[item.id]}
        open={Boolean(props.dropdownAnchors[item.id])}
        onClose={() => props.onDropdownClose(item.id)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionComponent={Fade}
      >
        {item.children?.map(child => (
          <MenuItem
            key={child.id}
            onClick={() => child.path && props.onNavigate(child.path)}
            selected={props.isCurrentPath(child)}
            disabled={!child.path}
            sx={{ display: 'flex', alignItems: 'center', '& *': { m: 0, p: 0 }, m: 0 }}
          >
            {child.icon && <ListItemIcon>{child.icon}</ListItemIcon>}
            <ListItemText>{child.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export function MobileNavigation(props: HeaderNavigationProps): JSX.Element {
  return (
    <List sx={{ pt: 0 }}>
      {props.navigationItems.map(item => (
        <MobileNavigationItem key={item.id} item={item} depth={0} {...props} />
      ))}
      <Divider sx={{ my: 1 }} />
      {!props.userExists && (
        <ListItem disablePadding>
          <ListItemButton onClick={() => props.onNavigate('/login')}>
            <ListItemText primary="Login" />
          </ListItemButton>
        </ListItem>
      )}
    </List>
  );
}

function MobileNavigationItem(
  props: HeaderNavigationProps & { item: NavigationItem; depth: number }
): JSX.Element {
  const { item, depth } = props;
  const hasChildren = item.children && item.children.length > 0;
  const isActive = props.isCurrentPath(item) || props.hasActiveChild(item);
  const isExpanded = props.mobileExpanded[item.id];

  return (
    <Box key={item.id}>
      <ListItem disablePadding sx={{ pl: depth * 2 }}>
        <ListItemButton
          onClick={() =>
            hasChildren ? props.onMobileToggle(item.id) : item.path && props.onNavigate(item.path)
          }
          selected={isActive}
          sx={{
            backgroundColor: isActive ? 'action.selected' : 'transparent',
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
            },
          }}
        >
          {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
          <ListItemText
            primary={item.label}
            sx={{
              '& .MuiTypography-root': {
                fontSize: depth > 0 ? '0.875rem' : '1rem',
                fontWeight: depth === 0 ? 500 : 400,
              },
            }}
          />
          {hasChildren && (
            <IconButton size="small">{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
          )}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children?.map(child => (
              <MobileNavigationItem {...props} key={child.id} item={child} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
}
